import { call, all, put, select, takeLatest } from 'redux-saga/effects';
import { delay } from 'redux-saga';
import {
  getModel,
  actions as macheteActions,
  utils as macheteReduxUtils,
} from 'src/machete-redux';
import trackError from 'src/lib/rollbar';
import utils from 'src/utils';
import appDux from 'src/redux/app';
import splitDux from 'src/redux/split';
import trackingDux from 'src/redux/tracking';
import constants from 'src/constants';
import EventNames from 'src/lib/eventNames';
import undoDux from 'src/redux/undo';
import createDebug from 'debug';
import { applyAttributeToNodes } from '../app/sagas';

const debug = createDebug('designPro.split.sagas');

const { SPLIT_MODES } = constants.app;
const { types: errorModalTypes } = constants.errorModals;
const {
  CHANGE_SPLIT_TOOL_ORIENTATION_FAILED,
  CONFIRM_DELETE_SPLIT_FAILED,
  CONFIRM_SPLIT_FAILED,
  DRAW_SPLIT_INDICATOR_FAILED,
  INITIALIZE_SPLIT_TOOL_FAILED,
  SET_SPLIT_RANGE_FAILED,
  UPDATE_SPLIT_FACADE_FAILED,
  CORRUPT_SEGMENTATION_TREE,
} = constants.errors;
const {
  addSplitIndicatorGroup,
  clearSplitIndicatorLine,
  deleteMacheteNode,
  deleteOverlappingChildSplits,
  drawIndicator,
  getHeightRangeForNodeElements,
  getOrientationProperty,
  getSplitChildUUID,
  getSplitRange,
} = utils.split;
const { sequenceSagaEffect: sequence } = utils.redux;
const { metersToInches } = Hover.ConversionUtils;

export function* setSplitRangeSaga() {
  try {
    const uuids = yield select(splitDux.selectors.getSplitToolUUIDSToSplit);
    const isEditingSplit = yield select(splitDux.selectors.isEditingSplit);
    const orientation = yield select(splitDux.selectors.getSplitOrientation);
    const { minOffset, maxOffset } = getSplitRange(
      uuids,
      orientation,
      isEditingSplit,
    );
    yield put(
      splitDux.actions.setSplitToolOffsetRange({ minOffset, maxOffset }),
    );
  } catch (e) {
    yield put(appDux.actions.activateErrorModal());
    trackError(SET_SPLIT_RANGE_FAILED, e);
  }
}

/**
 * Recalculate split range when user changes the split orientation. Also
 * set range back to mid-point (so indicator redraws)
 */
export function* changeSplitToolOrientationSaga() {
  try {
    yield call(setSplitRangeSaga);
    yield put(splitDux.actions.handleSplitToolOffsetChange(0.5));
  } catch (e) {
    yield put(appDux.actions.activateErrorModal());
    trackError(CHANGE_SPLIT_TOOL_ORIENTATION_FAILED, e);
  }
}

/**
 * Adds the split indicator group and sets the slider min/max values
 */
function* initializeSplitToolSaga() {
  try {
    addSplitIndicatorGroup();
    const uuids = yield select(splitDux.selectors.getSplitToolUUIDSToSplit);
    const isEditingSplit = yield select(splitDux.selectors.isEditingSplit);
    let orientation;
    if (isEditingSplit) {
      orientation = getOrientationProperty(uuids[0]);
    } else {
      orientation = SPLIT_MODES.HORIZONTAL;
    }
    yield put(splitDux.actions.changeSplitToolOrientation(orientation));
    yield call(setSplitRangeSaga);

    yield put(splitDux.actions.handleSplitToolOffsetChange(0.5));
  } catch (e) {
    yield put(appDux.actions.activateErrorModal());
    trackError(INITIALIZE_SPLIT_TOOL_FAILED, e);
  }
}

/**
 * Clears / redraws the split indicator line based on the new height
 */
function* drawSplitIndicatorSaga() {
  try {
    clearSplitIndicatorLine();
    const { minOffset, maxOffset } = yield select(
      splitDux.selectors.getSplitMinMaxOffset,
    );
    const splitPercentage = yield select(splitDux.selectors.getSplitPercentage);
    const offset = minOffset + splitPercentage * (maxOffset - minOffset);

    const orientation = yield select(splitDux.selectors.getSplitOrientation);
    const uuids = yield select(splitDux.selectors.getSplitToolUUIDSToSplit);
    const isEditingSplit = yield select(splitDux.selectors.isEditingSplit);
    drawIndicator(offset, orientation, uuids, isEditingSplit);
  } catch (e) {
    yield put(appDux.actions.activateErrorModal());
    trackError(DRAW_SPLIT_INDICATOR_FAILED, e);
  }
}

/**
 * Updates a split facade's properties based on the splitPercentage value
 * from the ui store, the min/max height of the slider element, and the
 * dimensions of the parent elements.
 *
 * returns boolean whether the node was able to be split at the selected
 * height
 */
function* updateSplitFacadeSaga(splitUUID, parentUUID) {
  try {
    const isEditingSplit = yield select(splitDux.selectors.isEditingSplit);
    const orientation = yield select(splitDux.selectors.getSplitOrientation);
    const { minOffset, maxOffset } = yield select(
      splitDux.selectors.getSplitMinMaxOffset,
    );

    // bounds of the parent node we're splitting
    let elMaxOffset;
    let elMinOffset;
    if (orientation === SPLIT_MODES.HORIZONTAL) {
      const { maxHeight, minHeight } = getHeightRangeForNodeElements(
        parentUUID,
      );
      elMaxOffset = maxHeight;
      elMinOffset = minHeight;
    } else {
      elMaxOffset = maxOffset;
      elMinOffset = minOffset;
    }

    // bail if the split line isn't within the bounds of parent element
    const splitPercentage = yield select(splitDux.selectors.getSplitPercentage);
    const splitOffsetMeters =
      minOffset + splitPercentage * (maxOffset - minOffset);
    if (splitOffsetMeters < elMinOffset || splitOffsetMeters > elMaxOffset) {
      return false;
    }

    // The `size` property of a machete 'split' type node is
    // specified as # of inches from the top or left of the element.
    const splitOffsetInches = metersToInches(splitOffsetMeters);
    const elMaxOffsetInches = metersToInches(elMaxOffset);
    const sizeUnrounded =
      orientation === SPLIT_MODES.HORIZONTAL
        ? elMaxOffsetInches - splitOffsetInches
        : splitOffsetInches;
    const size = Math.round(sizeUnrounded);

    if (isEditingSplit) {
      deleteOverlappingChildSplits(splitUUID, splitOffsetMeters);
    }

    yield put(
      macheteActions.segmentation.updateNode(splitUUID, {
        present: true,
        orientation,
        size,
      }),
    );

    if (!isEditingSplit) {
      const nodeStylesMap = yield select(appDux.selectors.getNodeStylesMap);
      const { uuid, ...styleAttributes } = nodeStylesMap[parentUUID];
      yield put(
        appDux.actions.addSplitNodeStyles([splitUUID], styleAttributes),
      );
      macheteReduxUtils.addSplitLevelTo(splitUUID);
    }

    return true;
  } catch (e) {
    yield put(appDux.actions.activateErrorModal());
    trackError(UPDATE_SPLIT_FACADE_FAILED, e);
  }
}

/**
 * Gathers the tracked data for the split event and fires the event
 */
export function* trackConfirmSplit(facadeCount) {
  const orientation = yield select(splitDux.selectors.getSplitOrientation);
  const eventData = {
    facadeCount,
    orientation,
  };
  yield put(
    trackingDux.actions.track(
      EventNames.viewer.FACADE_SPLIT_CONFIRMED,
      eventData,
    ),
  );
}

/**
 * Gets the UUIDS for the 'split' child nodes of the nodes that were
 * selected to be split, calls updateSplitFacadeSaga for each of them, then
 * tries to select the parent node (since of the two, that is the one
 * that appears underneath)
 */
export function* confirmSplitSaga() {
  try {
    const parentUUIDs = yield select(
      splitDux.selectors.getSplitToolUUIDSToSplit,
    );

    // create and run an array of tasks to run to adjust the node sizes
    const sizeSplitNodeTasks = parentUUIDs
      .map((uuid) => [getSplitChildUUID(uuid), uuid])
      .map(([splitUUID, parentUUID]) =>
        call(updateSplitFacadeSaga, splitUUID, parentUUID),
      );
    const splitNodeTaskResults = yield sequence(sizeSplitNodeTasks);

    // get the parent IDs of the nodes that were able to be split
    const splitParentNodeIds = parentUUIDs.filter(
      (_, index) => splitNodeTaskResults[index],
    );

    // short delay to allow node selection to occur
    yield call(delay, 100);

    // determine which nodes to select
    const shouldSelectChildren = yield select(
      splitDux.selectors.shouldSelectChildrenAfterSplit,
    );
    const nodesToSelect = shouldSelectChildren
      ? splitParentNodeIds.map(getSplitChildUUID)
      : splitParentNodeIds;

    // select those nodes and clear the split line
    yield put(macheteActions.segmentation.setNodeSelection(nodesToSelect));
    clearSplitIndicatorLine();
    yield call(trackConfirmSplit, nodesToSelect.length);
  } catch (e) {
    yield put(appDux.actions.activateErrorModal());
    trackError(CONFIRM_SPLIT_FAILED, e);
  }
}

export function* confirmDeleteSplitSaga(action) {
  try {
    const parentUUIDs = yield select(
      splitDux.selectors.getSplitToolUUIDSToSplit,
    );
    const nodeStylesMap = yield select(appDux.selectors.getNodeStylesMap);
    const splitToDeleteUUIDs = parentUUIDs.map(getSplitChildUUID);
    const parentAndSplitUUIDs = parentUUIDs.map((uuid, index) => [
      uuid,
      splitToDeleteUUIDs[index],
    ]);
    const splitToDeleteUUIDSet = new Set(splitToDeleteUUIDs);
    const parentSplitMap = new Map(parentAndSplitUUIDs);
    const styleUpdates = {};

    // perform the deletions
    splitToDeleteUUIDs.map(deleteMacheteNode);
    // trigger removal of any undo/redo entries referencing these uuids
    yield put(undoDux.actions.removeDeletedSplitEntries(splitToDeleteUUIDs));

    for (const parentUUID of parentUUIDs) {
      const splitToDeleteUUID = parentSplitMap.get(parentUUID);
      const { uuid: _, ...splitStyle } = nodeStylesMap[splitToDeleteUUID];
      const styleUpdate = { ...splitStyle, uuid: parentUUID };

      styleUpdates[parentUUID] = styleUpdate;
      yield put(appDux.actions.replaceNodeStyles([parentUUID], splitStyle));
    }

    const newStyles = { ...nodeStylesMap, ...styleUpdates };
    const filtered = Object.values(newStyles).filter(
      ({ uuid }) => !splitToDeleteUUIDSet.has(uuid),
    );

    yield put(appDux.actions.setNodeStyles(filtered));

    clearSplitIndicatorLine();
    yield put(macheteActions.segmentation.setNodeSelection(parentUUIDs));
  } catch (e) {
    debug('confirm delete split saga error: ', e);
    yield put(appDux.actions.activateErrorModal());
    trackError(CONFIRM_DELETE_SPLIT_FAILED, e);
  }
}

/**
 * Main Saga
 */
export default function* sagas() {
  yield all([
    takeLatest(splitDux.actions.changeSplitToolOrientation, setSplitRangeSaga),
    takeLatest(splitDux.actions.handleSplitToolClick, initializeSplitToolSaga),
    takeLatest(
      [
        splitDux.actions.handleSplitToolOffsetChange,
        splitDux.actions.handleCancelDeleteSplit,
      ],
      drawSplitIndicatorSaga,
    ),
    takeLatest(
      splitDux.actions.changeSplitToolOrientation,
      changeSplitToolOrientationSaga,
    ),
    takeLatest(splitDux.actions.handleSplitToolConfirmSplit, confirmSplitSaga),
    takeLatest(
      [
        splitDux.actions.handleSplitToolCancelSplit,
        splitDux.actions.handleDeleteSplitClick,
      ],
      clearSplitIndicatorLine,
    ),
    takeLatest(
      splitDux.actions.handleConfirmDeleteSplit,
      confirmDeleteSplitSaga,
    ),
  ]);
}
